import { logError, type Maybe, createShortUid } from '@amazon/vinyl-util'
import { ctx } from '../env/playbackContext'
import type { PodcastEpisodeFields } from '../gql/podcastEpisodeFieldsFrag'
import { getLargestImage } from '../util/imageUtil'
import { podcastVideoUrlToLoadOptions } from './hlsSupport'
import type { PlaylistEntry } from './PlaylistController'
import { getPodcastEpisodeVideoInfo } from './podcastEpisodeVideoInfo'

const { target } = ctx('podcastEpisodeToPlaylistEntry')

/**
 * Convert a `PodcastEpisode` GraphQL node into a `PlaylistEntry` so it can be
 * loaded by the player.
 *
 * Video episodes (a resolved `videoUrl` + `videoStreamingFormat`) route to the
 * video stage via `video: true` on every browser — Vinyl's MSE HLS engine
 * plays the manifest cross-browser, so there's no Safari gate. Non-video
 * episodes take the plain audio path unchanged; the audio fallback only fires
 * when there's genuinely no video stream.
 */
export function podcastEpisodeToPlaylistEntry(
    episode: Maybe<PodcastEpisodeFields>
): PlaylistEntry | null {
    if (!episode) return null
    const { hasVideo, videoUrl, videoStreamingFormat } = getPodcastEpisodeVideoInfo(episode)
    const playVideo = hasVideo && videoStreamingFormat != null

    const audioUrl = episode.playbackInformation?.audioUrl
    if (!playVideo && !audioUrl) {
        logError(target, 'episode missing playable url', episode.id)
        return null
    }

    const image = getLargestImage(episode.images, 'PRIMARY')
    return {
        id: episode.id ?? createShortUid(),
        entity: episode,
        title: episode.title ?? '',
        albumTitle: episode.podcast?.title ?? '',
        artistName: episode.podcast?.author ?? '',
        duration: episode.duration ?? 0,
        image,
        ...(playVideo ? { video: true } : null),
        trackLoadOptions: playVideo
            ? podcastVideoUrlToLoadOptions(videoUrl!)
            : { type: 'src', uri: audioUrl! },
    }
}
